import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  articles,
} from '@/mocks/articles';
import {
  resolveSeo,
  buildSiteOrigin,
  SITE_NAME,
  SITE_LOGO,
  DEFAULT_ISO_DATE,
  type Lang,
} from './seoData';

function buildSchemas(pathname: string): object[] {
  const meta = resolveSeo(pathname);
  const origin = buildSiteOrigin();
  const canonical = `${origin}${meta.canonicalPath}`;
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const parts = normalized.split('/').filter(Boolean);
  const lang: Lang = meta.lang;

  // Home page
  const isHome = parts.length === 0 || (parts.length === 1 && (parts[0] === 'en' || parts[0] === 'pt' || parts[0] === 'de'));
  if (isHome) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: `${origin}/`,
        logo: SITE_LOGO,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: `${origin}/`,
        inLanguage: lang,
        description: meta.description,
      },
    ];
  }

  // Article page
  if (parts.length === 3 && parts[1] === 'blog') {
    const article = articles.find((a) => a.slug === parts[2]);
    if (article) {
      const content = article[lang] || article.en;
      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: content.title,
        description: content.metaDesc,
        image: article.image,
        datePublished: DEFAULT_ISO_DATE,
        dateModified: DEFAULT_ISO_DATE,
        inLanguage: lang,
        author: {
          '@type': 'Organization',
          name: SITE_NAME,
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: {
            '@type': 'ImageObject',
            url: SITE_LOGO,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonical,
        },
      };
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: content.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a,
          },
        })),
      };
      return [articleSchema, faqSchema];
    }
  }

  // Legal / about pages
  if (parts.length === 2) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: meta.title,
        description: meta.description,
        url: canonical,
        inLanguage: lang,
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: `${origin}/`,
        },
      },
    ];
  }

  return [];
}

export default function JsonLd() {
  const location = useLocation();

  useEffect(() => {
    document
      .querySelectorAll('script[data-seo-jsonld]')
      .forEach((el) => el.remove());

    const schemas = buildSchemas(location.pathname);
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document
        .querySelectorAll('script[data-seo-jsonld]')
        .forEach((el) => el.remove());
    };
  }, [location.pathname]);

  return null;
}